import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { newsItems, newsCategories, tagColors } from "@/mocks/news";
import { useStaggerAnimation, useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Link } from "react-router-dom";
import PageSEO from "@/components/base/PageSEO";

export default function News() {
  const [activeCategory, setActiveCategory] = useState("全部");
  const headerAnim = useScrollAnimation();
  const cardsAnim = useStaggerAnimation(newsItems.length, { threshold: 0.05 });

  const filtered = activeCategory === "全部"
    ? newsItems
    : newsItems.filter(n => n.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <PageSEO
        title="最新消息｜台中女裝Outlet新品到貨 換季特賣活動"
        description="掌握台中設計師女裝Outlet最新消息，包含新品到貨、換季特賣、限量活動與門市公告，讓您第一時間入手高質感熟齡女裝。"
        keywords="台中女裝最新消息,設計師品牌新品,換季特賣活動,台中服飾優惠"
        canonical="https://www.example.com/news"
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[45vh] min-h-[340px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20taiwanese%20women%20fashion%20boutique%20news%20announcement%20warm%20cream%20beige%20interior%20designer%20brand%20new%20arrivals%20quality%20clothing%20display%20soft%20light&width=1400&height=650&seq=newshero&orientation=landscape"
            alt="最新消息"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-[#f5d8b8] text-[1rem] tracking-[0.3em] uppercase font-medium mb-3">Latest News</p>
            <h1 className="text-white text-[2.75rem] md:text-[3.25rem] font-bold leading-[1.3] mb-3">最新消息</h1>
            <p className="text-white/80 text-[1.1875rem] max-w-lg leading-[1.75]">掌握最新新品到貨、門市活動與限量優惠資訊，讓質感生活第一時間觸手可及。</p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="bg-white border-b border-[#ede8e0] sticky top-[72px] z-30">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="flex items-center gap-2 py-4 overflow-x-auto">
              {newsCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-[1rem] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${activeCategory === cat ? "bg-[#3d2b1f] text-[#faf8f5]" : "bg-[#f5ede4] text-[#7a6455] hover:bg-[#e8d8c8]"}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* News Grid */}
        <section className="py-16 md:py-24">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            {/* Featured Top */}
            {activeCategory === "全部" && (
              <Link
                to={`/news/${newsItems[0].id}`}
                ref={headerAnim.ref as React.RefObject<HTMLAnchorElement>}
                className={`mb-12 group rounded-lg overflow-hidden bg-white cursor-pointer block transition-all duration-700 ${headerAnim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative h-[300px] lg:h-auto overflow-hidden">
                    <img
                      src={newsItems[0].image}
                      alt={newsItems[0].title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className={`absolute top-4 left-4 px-3 py-1.5 rounded text-[0.9375rem] font-semibold ${tagColors[newsItems[0].tag] || "bg-[#c9a98a] text-[#3d2b1f]"}`}>
                      {newsItems[0].tag}
                    </span>
                  </div>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <p className="text-[#8B5E52] text-[1rem] font-medium mb-3">{newsItems[0].category}</p>
                    <h2 className="text-[#3d2b1f] text-[1.625rem] md:text-[2rem] font-bold leading-[1.4] mb-4 group-hover:text-[#8B5E52] transition-colors">{newsItems[0].title}</h2>
                    <p className="text-[#7a6455] text-[1.1875rem] leading-[1.75] mb-6">{newsItems[0].excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[#a09080] text-[1rem]">{newsItems[0].date}</span>
                      <span className="inline-flex items-center gap-1.5 text-[#8B5E52] text-[1rem] font-semibold group-hover:gap-2.5 transition-all">
                        閱讀更多 <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Count Label */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-[#7a6455] text-[0.9375rem]">
                {activeCategory === "全部"
                  ? <><span className="text-[#3d2b1f] font-bold">{filtered.length}</span> 則消息</>
                  : <>共 <span className="text-[#8B5E52] font-bold">{filtered.length}</span> 則 <span className="font-semibold text-[#3d2b1f]">{activeCategory}</span></>
                }
              </p>
            </div>

            {/* News Cards Grid */}
            <div
              ref={cardsAnim.ref as React.RefObject<HTMLDivElement>}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {(activeCategory === "全部" ? filtered.slice(1) : filtered).map((news, i) => (
                <Link
                  key={news.id}
                  to={`/news/${news.id}`}
                  className={`group bg-white rounded-lg overflow-hidden cursor-pointer block transition-all duration-700 ${cardsAnim.visibleItems[i] !== false ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                >
                  <div className="relative h-[220px] overflow-hidden">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className={`absolute top-3 left-3 px-2.5 py-1 rounded text-[0.875rem] font-semibold ${tagColors[news.tag] || "bg-[#c9a98a] text-[#3d2b1f]"}`}>
                      {news.tag}
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="text-[#8B5E52] text-[0.9375rem] font-medium mb-2">{news.category}</p>
                    <h3 className="text-[#3d2b1f] text-[1.125rem] font-bold leading-[1.45] mb-3 line-clamp-2 group-hover:text-[#8B5E52] transition-colors">{news.title}</h3>
                    <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75] mb-4 line-clamp-3">{news.excerpt}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-[#ede8e0]">
                      <span className="text-[#a09080] text-[0.9375rem]">{news.date}</span>
                      <span className="text-[#8B5E52] text-[0.9375rem] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        閱讀更多 <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Subscribe CTA */}
        <section className="py-16 bg-[#f5ede4] text-center px-6">
          <h2 className="text-[#3d2b1f] text-[2rem] md:text-[2.5rem] font-bold mb-4">不錯過任何新品與活動</h2>
          <p className="text-[#7a6455] text-[1.1875rem] mb-8 max-w-lg mx-auto leading-[1.75]">加入 LINE 官方帳號，第一時間接收新品到貨、限量活動與換季特賣通知。</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://line.me" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-3.5 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1.0625rem] font-bold hover:bg-[#8B5E52] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-line-fill"></i> 加入 LINE 接收通知
            </a>
            <Link to="/contact" className="px-8 py-3.5 border-2 border-[#3d2b1f] text-[#3d2b1f] rounded-full text-[1.0625rem] font-semibold hover:bg-[#3d2b1f] hover:text-[#faf8f5] transition-colors cursor-pointer whitespace-nowrap">
              查看門市資訊
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
